import { MouseEvent, useState } from 'react'
import { ExternalLink } from '@digital-twins/design-system/icons'
import { WebCitation } from '@digital-twins/shared-types'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import i18n, { intlLocaleForUi } from '@/i18n'
import { CitationImageView, compactCitationDomain, hostnameFromUrl, httpsImageSrc, isHoverlessDevice, SourceFavicon } from '@/components/citations'

// Moved to the shared citation module; re-exported with the same names and
// signatures for existing consumers (ThreadFurtherReading among them).
export { faviconUrlForHostname, hostnameFromUrl, SourceFavicon } from '@/components/citations'

/**
 * One chip per URL: several web citations pointing at the same page collapse
 * into a single group so the same source is never listed twice. Mirrors
 * `CitationDocumentGroup` / `groupCitationsByDocument`, keyed on `url`
 * instead of a document token — a web citation has no document row to key on
 * (see `partitionAssistantCitations.ts`).
 *
 * The chip stays small (favicon + publisher or host). Title, date and
 * destination live on hover. Favicons come from the hostname via Google's
 * public icon endpoint — no page fetch, no new citation field. A failed
 * load falls back to the globe so a broken image never sits in the pill.
 */
export interface WebCitationGroup {
  key: string
  citations: Array<WebCitation>
}

export function groupWebCitationsByUrl(
  citations: Array<WebCitation>,
): Array<WebCitationGroup> {
  const groups: Array<WebCitationGroup> = []
  const indexByKey = new Map<string, number>()

  citations.forEach((citation) => {
    const key = citation.url || citation.title || 'web-source'
    const existing = indexByKey.get(key)
    if (existing == null) {
      indexByKey.set(key, groups.length)
      groups.push({ key, citations: [citation] })
      return
    }
    groups[existing].citations.push(citation)
  })

  return groups
}

export function formatPublishedDate(value: string | undefined): string | null {
  if (!value) {
    return null
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return date.toLocaleDateString(intlLocaleForUi(i18n.language), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function chipLabel(citation: WebCitation, hostname: string | null): string {
  return compactCitationDomain(citation.publisher?.trim() || hostname || citation.title?.trim() || citation.url || i18n.t('threads.scope.chipWeb', { ns: 'app' }))
}

interface ThreadWebSourceCardProps {
  group: WebCitationGroup
  /** Position in the answer's web source list, for test ids. */
  index: number
}

export function ThreadWebSourceCard(props: ThreadWebSourceCardProps) {
  const { group, index } = props

  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [hasImageError, setHasImageError] = useState(false)

  const first = group.citations[0]

  if (!first) {
    return null
  }

  const hostname = hostnameFromUrl(first.url)
  const publisherLabel = first.publisher?.trim() || hostname
  const dateLabel = formatPublishedDate(first.publishedAt)
  const metaParts = [publisherLabel, dateLabel].filter(
    (value): value is string => Boolean(value),
  )
  const title = first.title?.trim() || first.url
  const label = chipLabel(first, hostname)
  // This URL-level preview identifies a page, not a claim-specific passage.
  // Search snippets remain stored evidence; inline citations own their excerpts.
  // The page's own image (og:image), when the web evidence carried one; https only.
  const imageSrc = httpsImageSrc((first as WebCitation & { imageUrl?: unknown }).imageUrl)

  const thumbnail = imageSrc && !hasImageError ? (
    <CitationImageView
      image={{ kind: 'url', src: imageSrc }}
      alt={title}
      className="h-12 w-12 shrink-0 rounded object-cover"
      testId={`thread-web-source-card-image-${index}`}
      onError={() => setHasImageError(true)}
    />
  ) : null

  const preview = (
    <div className="space-y-2" data-testid="thread-web-source-card-preview">
      <div className="flex items-start gap-2">
        <Heading level={3} size="sm" weight="medium" className="min-w-0 flex-1 leading-snug">
          {title}
        </Heading>
        {thumbnail}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
        <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
        <Text as="span" size="xs" color="muted">
          {metaParts.length > 0 ? metaParts.join(' · ') : 'Web'}
        </Text>
      </div>
    </div>
  )

  const chipClassName =
    'inline-flex max-w-[12rem] items-center gap-1.5 rounded-full bg-background dark:bg-muted px-2.5 py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/30'

  // Touch has no hover: the first tap shows the page preview, the
  // next one follows the link.
  const onLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isPreviewOpen && isHoverlessDevice()) {
      event.preventDefault()
      setIsPreviewOpen(true)
    }
  }

  const chipInner = (
    <>
      <SourceFavicon hostname={hostname} />
      <Text as="span" size="xs" className="truncate">
        {label}
      </Text>
    </>
  )

  const trigger = first.url ? (
    // eslint-disable-next-line react/jsx-no-target-blank -- `noopener` alone already closes the tabnabbing vector this rule guards against; `noreferrer` is deliberately omitted so the source publisher still sees it was reached from here.
    <a
      href={first.url}
      target="_blank"
      rel="noopener"
      className={chipClassName}
      data-testid="thread-web-source-card-link"
      onClick={onLinkClick}
    >
      {chipInner}
    </a>
  ) : (
    <span className={chipClassName}>{chipInner}</span>
  )

  return (
    <article
      className="inline-flex"
      data-testid="thread-web-source-card"
      data-url-key={group.key}
    >
      <HoverCard openDelay={150} closeDelay={120} open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <HoverCardTrigger asChild>{trigger}</HoverCardTrigger>
        <HoverCardContent
          align="start"
          side="top"
          sideOffset={8}
          className="w-80"
        >
          {preview}
        </HoverCardContent>
      </HoverCard>
    </article>
  )
}
